import React, { createContext, useContext, useEffect, useState } from "react";
import useLocalStorageState from "@hooks/useLocalStorage.js";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import authService from "../services/authService";

// Crie o contexto
const AuthContext = createContext();

// Crie o provider
export function AuthProvider({ children }) {
  const [status, setStatus] = useLocalStorageState(
    "auth",
    "auth_status",
    "login"
  );
  const [token, setToken] = useLocalStorageState("auth", "auth_token", "");
  const [user, setUser] = useLocalStorageState("auth", "auth_user", "");
  const [projeto, setProjeto] = useLocalStorageState("projeto", {});
  const [projetos, setProjetos] = useLocalStorageState("projetos", {});
  const [isAdmin, setIsAdmin] = useState(false);

  // Função para fazer logout
  const logout = () => {
    authService.logout();
  };

  // Função para obter o projeto do usuário
  const getProjeto = async () => {
    try {
      const response = await axios.get(
        `https://dev.vidal-app.com/projetos/user`,
        {
          headers: {
            "user-id": user.id || user._id, // Garante compatibilidade com ambas propriedades
          },
        }
      );
      console.log("Projeto data: ", response.data);
      setProjetos(response.data);
    } catch (error) {
      console.error("Erro ao buscar o projeto:", error);
      // Se o erro for de autenticação, pode ser necessário fazer logout
      if (error.response && error.response.status === 401) {
        authService.sessionExpiredLogout();
      }
    }
  };

  // Efeito para configurar interceptores do Axios
  useEffect(() => {
    // Configurar interceptores para tratar erros de autenticação
    authService.setupAxiosInterceptors();
  }, []);

  // Efeito para verificar a expiração do token e configurar o temporizador
  useEffect(() => {
    if (token) {
      let decodedToken;
      try {
        decodedToken = jwtDecode(token);
      } catch (error) {
        console.error("Token inválido:", error);
        authService.sessionExpiredLogout();
        return;
      }

      // O campo "exp" no token geralmente vem em segundos
      const expirationTime = decodedToken.exp * 1000; // converter para milissegundos
      const currentTime = Date.now();
      const timeout = expirationTime - currentTime;

      if (timeout <= 0) {
        // Se o token já estiver expirado, faça o logout imediatamente.
        authService.sessionExpiredLogout();
      } else {
        // Configurar alertas para quando o token estiver próximo da expiração
        const warningTime = timeout - 300000; // 5 minutos antes da expiração

        // Alerta de aviso sobre a expiração iminente
        let warningTimer;
        if (warningTime > 0) {
          warningTimer = setTimeout(() => {
            toast.warning(
              "Sua sessão expirará em 5 minutos. Salve seu trabalho."
            );
          }, warningTime);
        }

        // Configurar um timer para deslogar o usuário quando o token expirar
        const expirationTimer = setTimeout(() => {
          authService.sessionExpiredLogout();
        }, timeout);

        // Limpar os timers se o componente for desmontado ou se o token mudar
        return () => {
          if (warningTimer) clearTimeout(warningTimer);
          clearTimeout(expirationTimer);
        };
      }
    }
  }, [token]);

  // Efeito para buscar projeto quando o usuário mudar
  useEffect(() => {
    if ((user._id || user.id) && !projeto._id) {
      getProjeto();
    }
  }, [user, projeto]);

  return (
    <AuthContext.Provider
      value={{
        status,
        setStatus,
        token,
        setToken,
        user,
        setUser,
        projeto,
        setProjeto,
        getProjeto,
        projetos,
        setProjetos,
        isAdmin,
        setIsAdmin,
        logout, // Adicionado a função de logout ao contexto
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Crie um hook para usar o contexto
export function useAuth() {
  return useContext(AuthContext);
}
